import React from "react";
import { useSelector } from "react-redux";
import { convertToAMPM } from "../../../../../Js/Date";
import { getFilteredCashCard, hasCashCardFilter, groupCashCardByType } from "../cashCardFilterUtils";

function BillTypeSummary({ showSubHeaders, paymentTypeSplit, cashCardSplit, cardNameFilter, bankJournalFilter }) {
  const ShiftBillTypeSummary = useSelector(
    (state) => state.posReportshiftSlice.billTypeSummary
  );
  const decimalPosition = localStorage.getItem("decimalPosition");

  const cashCardFilterActive = hasCashCardFilter(cardNameFilter, bankJournalFilter);
  const { rows: filteredCashCardRows, total: filteredCashCardTotal } = getFilteredCashCard(
    ShiftBillTypeSummary?.cashAndCard?.list,
    cardNameFilter,
    bankJournalFilter
  );
  const cashCardRowsToShow = cashCardFilterActive
    ? filteredCashCardRows
    : ShiftBillTypeSummary?.cashAndCard?.list || [];
  const cashCardGroups = groupCashCardByType(cashCardRowsToShow);

  const getPaymentTypeBreakdown = (list) => {
    if (!list || list.length === 0) return [];
    const map = {};
    list.forEach((item) => {
      const pt = item?.paymentType || "Unknown";
      map[pt] = (map[pt] || 0) + (item?.amount || 0);
    });
    return Object.entries(map).map(([type, amount]) => ({ type, amount }));
  };

  const staffExpBreakdown = paymentTypeSplit ? getPaymentTypeBreakdown(ShiftBillTypeSummary?.staffExpense?.list) : [];
  const outletExpBreakdown = paymentTypeSplit ? getPaymentTypeBreakdown(ShiftBillTypeSummary?.outletExpense?.list) : [];

  const getAllExpenseBreakdown = () => {
    const map = {};
    const allLists = [
      ...(ShiftBillTypeSummary?.staffExpense?.list || []),
      ...(ShiftBillTypeSummary?.outletExpense?.list || []),
    ];
    allLists.forEach((item) => {
      const pt = (item?.paymentType || "Unknown").toUpperCase();
      map[pt] = (map[pt] || 0) + (item?.amount || 0);
    });
    return map;
  };

  const getAfterExpensesRows = () => {
    if (!paymentTypeSplit) return [];
    const cashCardList = ShiftBillTypeSummary?.cashAndCard?.list || [];
    const expMap = getAllExpenseBreakdown();
    const rows = [];
    cashCardList.forEach((cc) => {
      const typeName = (cc?.type || "").toUpperCase();
      if (typeName === "CASH") return;
      const expAmount = expMap[typeName] || 0;
      if (expAmount > 0) {
        rows.push({
          type: `${cc?.type} After Expenses`,
          amount: (cc?.amount || 0) - expAmount,
        });
      }
    });
    return rows;
  };

  const afterExpensesRows = getAfterExpensesRows();

  const formatTxnDate = (row) => row?.date ?? "";

  const formatTxnTime = (row) => {
    if (!row) return "";

    const rawTime =
      row?.time ?? row?.orderTime ?? row?.billTime ?? row?.startTime ?? null;
    if (rawTime) {
      const ampm = convertToAMPM(rawTime);
      if (!ampm) return "";
      const [h, rest] = ampm.split(":");
      if (!rest) return ampm;
      return `${String(h).padStart(2, "0")}:${rest}`;
    }

    const createdAt = row?.createdAt ?? row?.orderCreatedAt ?? null;
    if (createdAt) {
      const d = new Date(createdAt);
      if (!Number.isNaN(d.getTime())) {
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        const ampm = convertToAMPM(`${hh}:${mm}`);
        if (!ampm) return "";
        const [h, rest] = ampm.split(":");
        if (!rest) return ampm;
        return `${String(h).padStart(2, "0")}:${rest}`;
      }
    }
    return "";
  };

  const formatTxnDateTime = (row) => {
    const d = formatTxnDate(row);
    const t = formatTxnTime(row);
    if (d && t) return `${d} ${t}`;
    return d || t || "";
  };
  const getBackgroundColor = (index) => {
    return index % 2 === 0 ? "#ffffff" : "#E6E6E6";
  };
  // Use backend's delivery.total which correctly sums aggregator and selfDelivery counts
  const deliveryTotal = ShiftBillTypeSummary?.delivery?.total || 0

  return (
    <div
      className="new-global-table-container"
      style={{ paddingTop: "0 42px" }}
    >
      <div className="bill-main-head">
        <h5>Bill Type</h5>
        <h5>Amount</h5>
      </div>
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(0),
        }}
      >
        <h5>Dine In</h5>
        <h5>
          {(ShiftBillTypeSummary?.dineIn?.total||0)?.toFixed(decimalPosition)}
        </h5>
      </div>
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Bill No</th>
              <th>Date</th>
              <th>Time</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.dineIn?.list !== undefined ? (
              ShiftBillTypeSummary?.dineIn?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r.billNo}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnDate(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnTime(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r.amount?.toFixed(decimalPosition)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(1),
        }}
      >
        <h5>Take Away</h5>
        <h5>
          {(ShiftBillTypeSummary?.takeAway?.total||0)?.toFixed(decimalPosition)}
        </h5>
      </div>
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Bill No</th>
              <th>Date</th>
              <th>Time</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.takeAway?.list !== undefined ? (
              ShiftBillTypeSummary?.takeAway?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r.billNo}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnDate(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnTime(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r.amount?.toFixed(decimalPosition)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(2),
        }}
      >
        <h5>Delivery</h5>
        <h5>
          {(deliveryTotal||0)?.toFixed(decimalPosition)}
        </h5>
      </div>
      {showSubHeaders && (
        <div className="bill-sub2-head">
          <h5>Self Delivery{ShiftBillTypeSummary?.selfDelivery?.count?`(${ShiftBillTypeSummary?.selfDelivery?.count})`:""}</h5>
          <h5> {(ShiftBillTypeSummary?.selfDelivery?.total || 0)?.toFixed(decimalPosition)}</h5>
        </div>
      )}
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Bill No</th>
              <th>Date</th>
              <th>Time</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.selfDelivery?.list !== undefined ? (
              ShiftBillTypeSummary?.selfDelivery?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r.billNo}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnDate(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnTime(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r.amount?.toFixed(decimalPosition)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {showSubHeaders && (
        <div className="bill-sub2-head">
          <h5>Aggregator{ShiftBillTypeSummary?.aggregator?.count?`(${ShiftBillTypeSummary?.aggregator?.count})`:""}</h5>
          <h5>{(ShiftBillTypeSummary?.aggregator?.total || 0)?.toFixed(decimalPosition)}</h5>
        </div>
      )}
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Aggregator</th>
              <th>Order ID</th>
              <th>Date</th>
              <th>Time</th>
              <th>Discount</th>
              <th>Commission</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.aggregator?.list !== undefined ? (
              ShiftBillTypeSummary?.aggregator?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.aggregatorName}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.billNo}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnDate(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnTime(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.discount?.toFixed(decimalPosition)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.commission}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.amount?.toFixed(decimalPosition)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(3),
        }}
      >
        <h5>Staff Expense</h5>
        <h5>
          {(ShiftBillTypeSummary?.staffExpense?.total||0)?.toFixed(decimalPosition) }
        </h5>
      </div>
      {!showSubHeaders && paymentTypeSplit && staffExpBreakdown.length > 0 && (
        <table>
          <tbody>
            {staffExpBreakdown.map((item, i) => (
              <tr key={i}>
                <td style={{ paddingLeft: "20px", height: "25px", fontSize: "13px", color: "#555" }}>
                  {item.type}
                </td>
                <td style={{ textAlign: "right", height: "25px", fontSize: "13px", color: "#555" }}>
                  {item.amount?.toFixed(decimalPosition)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Expense</th>
              <th>Employee Name</th>
              <th>Date</th>
              <th>Paid By</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.staffExpense?.list !== undefined ? (
              ShiftBillTypeSummary?.staffExpense?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.expense}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.employee}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnDate(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnTime(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.paymentType}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.amount?.toFixed(decimalPosition)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(4),
        }}
      >
        <h5>Outlet Expense</h5>
        <h5>
          {(ShiftBillTypeSummary?.outletExpense?.total || 0)?.toFixed(decimalPosition)}
        </h5>
      </div>
      {!showSubHeaders && paymentTypeSplit && outletExpBreakdown.length > 0 && (
        <table>
          <tbody>
            {outletExpBreakdown.map((item, i) => (
              <tr key={i}>
                <td style={{ paddingLeft: "20px", height: "25px", fontSize: "13px", color: "#555" }}>
                  {item.type}
                </td>
                <td style={{ textAlign: "right", height: "25px", fontSize: "13px", color: "#555" }}>
                  {item.amount?.toFixed(decimalPosition)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Shift ID</th>
              <th>Doc No</th>
              <th>Expense</th>
              <th>Date</th>
              <th>Paid By</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.outletExpense?.list !== undefined ? (
              ShiftBillTypeSummary?.outletExpense?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.shift}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.docNo}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.expense}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnDateTime(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.paymentType}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.amount?.toFixed(decimalPosition)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(5),
        }}
      >
        <h5>Refund</h5>
        <h5>
          {(ShiftBillTypeSummary?.refund?.total || 0)?.toFixed(decimalPosition)}
        </h5>
      </div>
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Order ID</th>
              <th>Product</th>
              <th>Type</th>
              <th>Date</th>
              <th>Time</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.refund?.list !== undefined ? (
              ShiftBillTypeSummary?.refund?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.billNo}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.product}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.type}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {formatTxnDateTime(r)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.amount?.toFixed(decimalPosition)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(6),
        }}
      >
        <h5>Cash & Card</h5>
        <h5>
          {(ShiftBillTypeSummary?.cashAndCard?.total ||0)?.toFixed(decimalPosition)}
        </h5>
      </div>
      {!showSubHeaders && cashCardSplit && cashCardGroups?.length > 0 && (
        <table>
          <tbody>
            {cashCardGroups.map((group, gi) => (
              <React.Fragment key={gi}>
                <tr style={{ backgroundColor: "#e8f5e9" }}>
                  <td style={{ paddingLeft: "20px", height: "25px", fontSize: "13px", color: "#555", fontWeight: "bold", textTransform: "capitalize" }}>
                    {group.type}
                  </td>
                  <td style={{ textAlign: "right", height: "25px", fontSize: "13px", color: "#555", fontWeight: "bold" }}>
                    {group.total?.toFixed(decimalPosition)}
                  </td>
                </tr>
                {group.items.map((r, i) => (
                  (r?.cardName || r?.journalName) && (
                    <tr key={`${gi}-${i}`}>
                      <td style={{ paddingLeft: "40px", height: "22px", fontSize: "12px", color: "#777" }}>
                        {[r?.cardName, r?.journalName].filter(Boolean).join(" - ")}
                      </td>
                      <td style={{ textAlign: "right", height: "22px", fontSize: "12px", color: "#777" }}>
                        {r?.amount?.toFixed(decimalPosition)}
                      </td>
                    </tr>
                  )
                ))}
              </React.Fragment>
            ))}
            {cashCardFilterActive && (
              <tr style={{ backgroundColor: "#c8e6c9" }}>
                <td style={{ paddingLeft: "20px", height: "25px", fontSize: "13px", color: "#222", fontWeight: "bold" }}>
                  Selected Total
                </td>
                <td style={{ textAlign: "right", height: "25px", fontSize: "13px", color: "#222", fontWeight: "bold" }}>
                  {filteredCashCardTotal?.toFixed(decimalPosition)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {!showSubHeaders && cashCardSplit && cashCardFilterActive && cashCardRowsToShow?.length === 0 && (
        <table>
          <tbody>
            <tr style={{ backgroundColor: "#e8f5e9" }}>
              <td colSpan={2} style={{ paddingLeft: "20px", height: "25px", fontSize: "13px", color: "#888" }}>
                No matching card / bank journal
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Payment Method</th>
              <th>Amount</th>
              <th>Commission</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.cashAndCard?.list !== undefined ? (
              ShiftBillTypeSummary?.cashAndCard?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.type}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.amount?.toFixed(decimalPosition)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.commission?.toFixed(decimalPosition)}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.balance?.toFixed(decimalPosition)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {ShiftBillTypeSummary?.cashAfterExpenses?.show && (
        <div
          className="bill-sub-head"
          style={{
            backgroundColor: "#d4edda",
          }}
        >
          <h5>Cash After Expenses</h5>
          <h5>
            {(ShiftBillTypeSummary?.cashAfterExpenses?.total || 0)?.toFixed(decimalPosition)}
          </h5>
        </div>
      )}
      {afterExpensesRows.length > 0 && afterExpensesRows.map((row, i) => (
        <div
          key={i}
          className="bill-sub-head"
          style={{
            backgroundColor: "#d4edda",
          }}
        >
          <h5 style={{ fontSize: "13px" }}>{row.type}</h5>
          <h5 style={{ fontSize: "13px" }}>
            {row.amount?.toFixed(decimalPosition)}
          </h5>
        </div>
      ))}
      {ShiftBillTypeSummary?.pettyCashInfo?.show && (
        <div
          className="bill-sub-head"
          style={{
            backgroundColor: "#fff3cd",
          }}
        >
          <h5>Petty Cash</h5>
          <h5>
            {(ShiftBillTypeSummary?.pettyCashInfo?.remaining || 0)?.toFixed(decimalPosition)}
          </h5>
        </div>
      )}
      {showSubHeaders && ShiftBillTypeSummary?.pettyCashInfo?.show && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Description</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ borderBottom: "1px solid black", height: "29px" }}>Balance</td>
              <td style={{ borderBottom: "1px solid black", height: "29px" }}>
                {(ShiftBillTypeSummary?.pettyCashInfo?.balance || 0)?.toFixed(decimalPosition)}
              </td>
            </tr>
            <tr>
              <td style={{ borderBottom: "1px solid black", height: "29px" }}>Spent</td>
              <td style={{ borderBottom: "1px solid black", height: "29px" }}>
                {(ShiftBillTypeSummary?.pettyCashInfo?.spent || 0)?.toFixed(decimalPosition)}
              </td>
            </tr>
            <tr>
              <td style={{ borderBottom: "1px solid black", height: "29px", fontWeight: "bold" }}>Remaining</td>
              <td style={{ borderBottom: "1px solid black", height: "29px", fontWeight: "bold" }}>
                {(ShiftBillTypeSummary?.pettyCashInfo?.remaining || 0)?.toFixed(decimalPosition)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(7),
        }}
      >
        <h5>Delivery Charge</h5>
        <h5>
          {(ShiftBillTypeSummary?.deliveryCharge?.total ||0)?.toFixed(decimalPosition)}
        </h5>
      </div>
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Bill No</th>
              <th>Amount</th>
            
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.deliveryCharge?.list !== undefined ? (
              ShiftBillTypeSummary?.deliveryCharge?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.billNo}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.amount?.toFixed(decimalPosition)}
                  </td>
                 
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <div
        className="bill-sub-head"
        style={{
          backgroundColor: showSubHeaders ? "#E6E6E6" : getBackgroundColor(8),
        }}
      >
        <h5>Packing Charge</h5>
        <h5>
          {(ShiftBillTypeSummary?.packagingCharge?.total ||0)?.toFixed(decimalPosition)}
        </h5>
      </div>
      {showSubHeaders && (
        <table>
          <thead style={{ backgroundColor: "#C0CFF8" }}>
            <tr>
              <th>Bill No</th>
              <th>Amount</th>
             
            </tr>
          </thead>
          <tbody>
            {ShiftBillTypeSummary?.packagingCharge?.list !== undefined ? (
              ShiftBillTypeSummary?.packagingCharge?.list?.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.billNo}
                  </td>
                  <td
                    style={{ borderBottom: "1px solid black", height: "29px" }}
                  >
                    {r?.amount?.toFixed(decimalPosition)}
                  </td>
                 
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>NO DATA</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default BillTypeSummary;
